/**
 * Shared analytics dashboard rendered inside each channel's Dashboard page.
 * KPI stat cards (delivery/read shown as ring progress), a daily-activity area
 * chart, and a recent-activity list — sourced from the cached Communications
 * analytics endpoint. Built from the component library (StatCard, AreaChartWidget).
 */

import { useEffect } from 'react';
import { IconMessages, IconSend, IconAlertTriangle, IconClock } from '@tabler/icons-react';
import { Stack, Group, Text, Title, Badge, Alert, Loader, SimpleGrid, Table } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { StatCard } from '../../../../components/advanced/dashboard';
import { AreaChartWidget } from '../../../../components/advanced/Widgets';
import { useCommunicationAnalytics } from './useCommunicationAnalytics';

function fmtDate(ymdHms: number): string {
  if (!ymdHms || ymdHms < 19000101000000) return '—';
  const s = String(Math.trunc(ymdHms)).padStart(14, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

function statusColor(status?: string | null): string {
  const s = (status || '').toLowerCase();
  if (s.includes('read')) return 'teal';
  if (s.includes('deliver')) return 'green';
  if (s.includes('fail') || s.includes('error') || s.includes('bounce') || s.includes('reject')) return 'red';
  if (s.includes('sent')) return 'blue';
  return 'gray';
}

const pct = (n: number) => `${Math.round((n || 0) * 100)}%`;
const rate = (n: number) => Math.max(0, Math.min(100, Math.round((n || 0) * 100)));

export function ChannelDashboard({ channel }: { channel: 'email' | 'sms' | 'whatsapp' | 'push' }) {
  const { loading, error, data, fetchChannel } = useCommunicationAnalytics();

  useEffect(() => {
    fetchChannel(channel, 8);
  }, [channel, fetchChannel]);

  if (loading && !data) {
    return (
      <Group justify="center" p="xl">
        <Loader size="lg" />
      </Group>
    );
  }

  if (error) {
    return <Alert color="red" title="Failed to load analytics">{error}</Alert>;
  }

  const s = data?.summary;
  const trendData = (data?.trends ?? []).map((t) => ({ date: t.bucket, Total: t.total, Failed: t.failed }));

  return (
    <Stack gap="lg">
      {/* KPI stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
        <StatCard
          size="sm"
          variant="gradient"
          gradientVariant="primary"
          title="Total"
          value={(s?.total ?? 0).toLocaleString()}
          icon={<IconMessages size={20} />}
        />
        <StatCard size="sm" title="Sent" value={(s?.sent ?? 0).toLocaleString()} icon={<IconSend size={20} />} />
        <StatCard
          size="sm"
          title="Delivered"
          value={(s?.delivered ?? 0).toLocaleString()}
          progress={{ value: rate(s?.deliveryRate ?? 0), label: pct(s?.deliveryRate ?? 0) }}
        />
        <StatCard
          size="sm"
          title="Read"
          value={(s?.read ?? 0).toLocaleString()}
          progress={{ value: rate(s?.readRate ?? 0), label: pct(s?.readRate ?? 0) }}
        />
        <StatCard
          size="sm"
          variant="outlined"
          title="Failed"
          value={(s?.failed ?? 0).toLocaleString()}
          icon={<IconAlertTriangle size={20} />}
        />
        <StatCard size="sm" title="Pending" value={(s?.pending ?? 0).toLocaleString()} icon={<IconClock size={20} />} />
      </SimpleGrid>

      {s?.capped && (
        <Alert color="yellow">
          Showing the most recent {s.windowSize.toLocaleString()} messages — older messages are not included in these counts.
        </Alert>
      )}

      {/* Daily activity */}
      {trendData.length === 0 ? (
        <Card withBorder padding="md">
          <Stack gap="xs">
            <Title order={5}>Daily activity</Title>
            <Text size="sm" c="dimmed">No activity in the window.</Text>
          </Stack>
        </Card>
      ) : (
        <AreaChartWidget
          title="Daily activity"
          subtitle="Messages per day"
          data={trendData}
          areas={[
            { dataKey: 'Total', name: 'Total', color: '#339af0', fillOpacity: 0.2 },
            { dataKey: 'Failed', name: 'Failed', color: '#fa5252', fillOpacity: 0.25 },
          ]}
          xAxisKey="date"
          height={260}
          showLegend
          showExport={false}
        />
      )}

      {/* Recent activity */}
      <Card withBorder padding="md">
        <Stack gap="sm">
          <Title order={5}>Recent activity</Title>
          {(data?.recent?.length ?? 0) === 0 ? (
            <Text size="sm" c="dimmed">No recent messages.</Text>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table variant="striped" verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Recipient</Table.Th>
                    {/* Push carries a notification title, not a subject line — and never the body. */}
                    <Table.Th>{channel === 'push' ? 'Title' : 'Subject'}</Table.Th>
                    <Table.Th style={{ width: 110 }}>Status</Table.Th>
                    <Table.Th style={{ width: 160 }}>Date</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data!.recent.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td><Text size="sm" truncate style={{ maxWidth: 220 }}>{r.recipient || '—'}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed" truncate style={{ maxWidth: 300 }}>{r.subject || '—'}</Text></Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={statusColor(r.status)}>{r.status || 'unknown'}</Badge>
                      </Table.Td>
                      <Table.Td><Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.createdDateTime)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
